import { useEffect, useMemo, useCallback } from 'react'
import { useLocalStorage } from 'usehooks-ts'
import { useSelectedDate, useSelectedDateDispatch } from '../SelectedDate'
import {
  SelectedRecordNameContext,
  SelectedRecordNameDispatchContext,
} from './context'
import {
  isCalendarNoteRecordName,
  parseCalendarNoteRecordName,
  deriveCalendarRecordName,
} from './utils'

export function SelectedRecordNameProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [storedRecordName, setStoredRecordName] = useLocalStorage(
    'selectedRecordName',
    'daily'
  )
  const selectedDate = useSelectedDate()
  const selectedDateDispatch = useSelectedDateDispatch()

  // Derive the current record name from selectedDate (primary source of truth)
  // for calendar notes, or return stored value for non-calendar notes
  const selectedRecordName = useMemo(() => {
    // If stored record name is NOT a calendar note, return it directly
    // This covers: 'search', 'filter-view', project note UUIDs, teamspace UUIDs, etc.
    if (!isCalendarNoteRecordName(storedRecordName)) {
      return storedRecordName
    }

    // For calendar notes, derive from selectedDate
    return deriveCalendarRecordName(selectedDate)
  }, [selectedDate, storedRecordName])

  // Custom setter that handles both special cases and calendar notes
  const setSelectedRecordName = useCallback(
    (recordName: string) => {
      // Handle non-calendar notes directly (search, project notes, teamspace notes, etc.)
      if (!isCalendarNoteRecordName(recordName)) {
        setStoredRecordName(recordName)
        return
      }

      // Handle calendar notes by updating selectedDate
      const { targetTimeframe, teamspaceId } = parseCalendarNoteRecordName(recordName)

      // Ensure we're in calendar mode so useMemo derives from selectedDate
      setStoredRecordName('daily')

      // Update selectedDate to the target timeframe (and teamspace)
      selectedDateDispatch({
        type: 'transform',
        to: targetTimeframe,
        teamspace: teamspaceId,
      })
    },
    [selectedDateDispatch, setStoredRecordName]
  )

  // Initialize selectedDate on mount if starting with a calendar note
  useEffect(() => {
    if (storedRecordName === 'daily') {
      selectedDateDispatch({ type: 'today' })
    } else if (isCalendarNoteRecordName(storedRecordName)) {
      const { targetTimeframe, teamspaceId } = parseCalendarNoteRecordName(storedRecordName)

      selectedDateDispatch({
        type: 'transform',
        to: targetTimeframe,
        teamspace: teamspaceId,
      })
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Keyboard shortcuts
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.metaKey && event.shiftKey && event.key === 'f') {
        setSelectedRecordName('search')
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [setSelectedRecordName])

  // Listen for navigate to daily event from SelectedDateProvider
  useEffect(() => {
    function handleNavigateToDaily() {
      setSelectedRecordName('daily')
    }
    window.addEventListener('navigateToDaily', handleNavigateToDaily)
    return () => {
      window.removeEventListener('navigateToDaily', handleNavigateToDaily)
    }
  }, [setSelectedRecordName])

  return (
    <SelectedRecordNameContext.Provider value={selectedRecordName}>
      <SelectedRecordNameDispatchContext.Provider value={setSelectedRecordName}>
        {children}
      </SelectedRecordNameDispatchContext.Provider>
    </SelectedRecordNameContext.Provider>
  )
}
